import { toSlotKey } from '../slotKey.js';
import { TicketState, toTicket, toTicketKey2 } from '../ticket.js';
import { nowAtSiteAsStr } from '../time.js';
import { emptyUser, toUser } from '../user.js';
import { toNumber } from '../utils/utils.js';
import { orderDocFromV0 } from './order.repo.v0.js';
var OrderState;
(function (OrderState) {
    OrderState["initial"] = "initial";
    OrderState["pendingPayment"] = "pending-payment";
    OrderState["reserved"] = "reserved";
    OrderState["booked"] = "booked";
    OrderState["cancelled"] = "cancelled";
    OrderState["expired"] = "expired";
    OrderState["credit"] = "credit";
})(OrderState || (OrderState = {}));
const toOrderEvent = (raw) => {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.map((r) => {
        const event = {
            eventId: r.eventId ?? '',
            subEventId: r.subEventId ?? '',
            price: toNumber(r.price),
            summary: r.summary ?? r.eventId ?? ''
        };
        // Only carried when a price was actually picked, so ordinary sign-ups
        // round-trip unchanged
        if (typeof r.priceOptionId === 'string' && r.priceOptionId !== '') {
            event.priceOptionId = r.priceOptionId;
            event.priceOptionLabel =
                typeof r.priceOptionLabel === 'string' ? r.priceOptionLabel : '';
        }
        // Same: absent on sign-ups that got no discount, and on orders written
        // before these fields existed
        if (typeof r.discountLabel === 'string' && r.discountLabel !== '') {
            event.discountLabel = r.discountLabel;
            event.basePrice = toNumber(r.basePrice);
        }
        return event;
    });
};
const toOrderTicket = (raw) => {
    const ticket = toTicket(raw);
    // Handle legacy conversion from old basePrice/punchCardId to unitPrice
    // Also fix missing unitPrice due to bug in toTicket
    if (!ticket.unitPrice) {
        ticket.unitPrice = {
            basePrice: 0,
            finalPrice: 0,
            punchCardId: '',
            clubCodeId: ''
        };
    }
    if (!raw.unitPrice && (raw.basePrice || raw.livePrice || raw.punchCardId)) {
        ticket.unitPrice.basePrice = raw.basePrice ?? ticket.unitPrice.basePrice;
        ticket.unitPrice.finalPrice = raw.livePrice ?? ticket.unitPrice.finalPrice;
        ticket.unitPrice.punchCardId =
            raw.punchCardId ?? ticket.unitPrice.punchCardId;
    }
    return ticket;
};
const toOrderDoc = (body) => {
    if (!body || Object.values(body).length === 0) {
        return emptyOrderDoc();
    }
    if (!body.version || body.version < 1) {
        return orderDocFromV0(body);
    }
    const order = emptyOrderDoc();
    if (body.version == 1 && body.items) {
        const items = Object.values(body.items);
        for (const item of items) {
            for (const ticketId of item.ticketIds) {
                const ticketKey = toTicketKey2(toSlotKey(item.itemKey), ticketId);
                order.tickets[ticketKey] = {
                    orderId: body.meta.orderId || '',
                    description: '',
                    clubCodeId: '',
                    punchCardId: '',
                    addedAt: '',
                    state: TicketState.unknown,
                    freeText: '',
                    reservedAt: '',
                    bookedAt: '',
                    holderPublicName: toUser(body.meta.user).publicName,
                    user: toUser(body.meta.user),
                    ticketKey: ticketKey,
                    totalPrice: item.pricePrTicket,
                    unitPrice: {
                        basePrice: item.pricePrTicket,
                        finalPrice: item.pricePrTicket,
                        punchCardId: '',
                        clubCodeId: ''
                    },
                    groupPrice: 0,
                    activityName: item.activityName,
                    groupBracket: [],
                    extras: []
                };
            }
        }
    }
    else {
        const rawTickets = Object.values(body.tickets ?? {});
        const tickets = rawTickets.map((raw) => [
            raw.ticketKey,
            toOrderTicket(raw)
        ]);
        order.tickets = Object.fromEntries(tickets);
    }
    order.meta = body.meta ?? order.meta;
    order.couponsToBuy = body.couponsToBuy ?? order.couponsToBuy;
    order.couponGiftInfo = body.couponGiftInfo ?? order.couponGiftInfo;
    order.couponsToApply = body.couponsToApply ?? order.couponsToApply;
    order.events = toOrderEvent(body.events ?? order.events);
    order.punchCardsToBuy = body.punchCardsToBuy ?? order.punchCardsToBuy;
    order.codesApplied = body.codesApplied ?? order.codesApplied;
    return order;
};
const emptyOrderDoc = () => {
    const meta = {
        user: emptyUser(),
        orderId: '',
        voucherNo: 0,
        reservedAt: '',
        bookedAt: '',
        state: OrderState.initial,
        totalPrice: 0,
        nofTicketsInOrder: 0,
        reference: '',
        paymentProvider: '',
        paymentId: '',
        paymentState: 'not-started',
        paymentDetails: [],
        accessCodes: []
    };
    return {
        meta,
        tickets: {},
        codesApplied: [],
        couponsToBuy: {},
        couponGiftInfo: {},
        couponsToApply: {},
        punchCardsToBuy: [],
        events: [],
        version: 3
    };
};
const orderSummary = (order) => {
    const tickets = Object.values(order.tickets);
    const couponsToBuy = Object.values(order.couponsToBuy);
    const hasCoupons = couponsToBuy.length > 0;
    const terms = [];
    if (tickets.length > 0) {
        const ticketTxt = tickets.length > 1 ? 'Billetter' : 'Billett';
        terms.push(ticketTxt);
    }
    if (hasCoupons) {
        terms.push('Gavekort');
    }
    if (order.events.length > 0) {
        terms.push('Arrangement');
    }
    const cardLimits = order.punchCardsToBuy.map((p) => p.definition.limitTickets);
    if (cardLimits.some((limit) => limit === 0)) {
        terms.push('Periodekort');
    }
    if (cardLimits.some((limit) => limit > 0)) {
        terms.push('Klippekort');
    }
    return terms.join(',');
};
/**
 * Calculate order sum before coupon deductions are applied.
 * This is the total of tickets + coupons to buy + events + punch cards.
 */
const orderSumBeforeCoupons = (order) => {
    const couponsToBuy = Object.values(order.couponsToBuy).reduce((sum, buy) => sum + Number(buy), 0);
    const tickets = Object.values(order.tickets).reduce((sum, i) => sum + toNumber(i.totalPrice), 0);
    const events = order.events.reduce((sum, event) => sum + toNumber(event.price), 0);
    const punchCards = order.punchCardsToBuy.reduce((sum, card) => sum + toNumber(card.definition.price), 0);
    return tickets + couponsToBuy + events + punchCards;
};
const orderTotal = (order) => {
    const sumPreDeductions = orderSumBeforeCoupons(order);
    const couponsToApply = Object.values(order.couponsToApply).reduce((sum, app) => sum + Number(app), 0);
    return sumPreDeductions - Math.min(couponsToApply, sumPreDeductions);
};
const orderMetaCreateUpdated = (order, state, voucherNo, orderId, paymentState, user, setTime) => {
    return {
        state,
        totalPrice: orderTotal(order),
        orderId,
        voucherNo,
        reference: orderSummary(order),
        reservedAt: setTime && state === OrderState.reserved
            ? nowAtSiteAsStr()
            : order.meta.reservedAt,
        bookedAt: setTime && state === OrderState.booked
            ? nowAtSiteAsStr()
            : order.meta.bookedAt,
        user,
        nofTicketsInOrder: order.meta.nofTicketsInOrder,
        paymentProvider: order.meta.paymentProvider,
        paymentId: order.meta.paymentId,
        paymentState,
        paymentDetails: order.meta.paymentDetails,
        accessCodes: []
    };
};
export { emptyOrderDoc, orderMetaCreateUpdated, OrderState, orderSumBeforeCoupons, orderSummary, orderTotal, toOrderDoc };
//# sourceMappingURL=order.js.map